import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";

export default function EULA() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to App
          </Button>
        </Link>

        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold mb-2">
            🧾 Cart Xray — End-User Licence Agreement (EULA)
          </h1>
          <h2 className="text-2xl font-bold mb-2">
            Conditions Générales d'Utilisation et de Licence (CGU-EULA)
          </h2>
          <p className="text-muted-foreground mb-8">
            <em>Last updated / Dernière mise à jour : October 2025</em>
          </p>

          <hr className="my-8" />

          <h2>1️⃣ Parties / Parties</h2>
          <p>
            This Agreement governs the use of the <strong>Cart Xray</strong>{" "}
            application, published by <strong>TS4U SAS</strong> (RCS
            Aix-en-Provence 929 710 952), having its registered office at 105
            chemin des Valladets, 13510 Éguilles, France.
            <br />
            Email : support.corp@cartxray.com
            <br />
            DPO : dpo.corp@cartxray.com
          </p>
          <p>
            Ce contrat régit l'utilisation de l'application{" "}
            <strong>Cart Xray</strong>, éditée par <strong>TS4U SAS</strong>{" "}
            (RCS Aix-en-Provence 929 710 952), 105 chemin des Valladets, 13510
            Éguilles, France.
            <br />
            Email : support.corp@cartxray.com
            <br />
            DPO : dpo.corp@cartxray.com
          </p>

          <hr className="my-8" />

          <h2>2️⃣ Purpose / Objet</h2>
          <p>
            Cart Xray allows users to upload and analyse purchase receipts to
            provide insights on spending, health, and environmental impact.
          </p>
          <p>
            Cart Xray permet aux utilisateurs de téléverser et d'analyser leurs
            tickets d'achat afin d'obtenir des informations sur leurs dépenses,
            la santé et l'impact environnemental.
          </p>

          <hr className="my-8" />

          <h2>3️⃣ Acceptance / Acceptation</h2>
          <p>
            By creating an account or using the app, you agree to this EULA and
            to the <strong>Privacy Policy / Politique de confidentialité</strong>{" "}
            (see <Link to="/privacy-policy">Privacy Policy</Link>).
          </p>

          <p>
            En créant un compte ou en utilisant l'application, vous acceptez les
            présentes CGU-EULA ainsi que la{" "}
            <strong>Politique de confidentialité</strong> (voir{" "}
            <Link to="/privacy-policy">Politique de confidentialité</Link>).
          </p>

          <hr className="my-8" />

          <h2>4️⃣ Licence / Licence d'utilisation</h2>
          <p>
            TS4U SAS grants a personal, non-exclusive, non-transferable licence
            to use Cart Xray for personal purposes only.
            <br />
            Reverse-engineering, resale, automated scraping or unauthorised
            copying are prohibited.
          </p>
          <p>
            TS4U SAS accorde une licence personnelle, non exclusive et non
            transférable d'utilisation de Cart Xray à des fins personnelles
            uniquement.
            <br />
            Toute rétro-ingénierie, revente, extraction automatisée ou copie non
            autorisée est interdite.
          </p>

          <hr className="my-8" />

          <h2>5️⃣ Accounts & Access / Comptes et accès</h2>
          <p>
            Users must provide accurate information and keep credentials
            confidential.
            <br />
            TS4U SAS may suspend or terminate accounts in case of misuse, fraud,
            or breach of terms.
          </p>
          <p>
            Les utilisateurs doivent fournir des informations exactes et
            conserver leurs identifiants confidentiels.
            <br />
            TS4U SAS peut suspendre ou résilier un compte en cas d'abus, fraude
            ou non-respect des CGU.
          </p>

          <hr className="my-8" />

          <h2>6️⃣ Pricing & Plans / Tarification et formules</h2>
          <p>Cart Xray offers several plans:</p>
          <table className="w-full border-collapse border border-border">
            <thead>
              <tr className="bg-muted">
                <th className="border border-border p-2 text-left">Plan</th>
                <th className="border border-border p-2 text-left">
                  Description
                </th>
                <th className="border border-border p-2 text-left">
                  Billing / Facturation
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-border p-2">
                  <strong>Trial / Essai</strong>
                </td>
                <td className="border border-border p-2">
                  Free, time-limited or receipt-limited
                </td>
                <td className="border border-border p-2">
                  Free — automatic expiry
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  <strong>Pay-as-you-go / À l'usage</strong>
                </td>
                <td className="border border-border p-2">
                  Purchase bundles of receipts ("credits")
                </td>
                <td className="border border-border p-2">
                  Pay per bundle, no renewal
                </td>
              </tr>
              <tr>
                <td className="border border-border p-2">
                  <strong>Subscription / Abonnement</strong>
                </td>
                <td className="border border-border p-2">
                  Monthly or annual recurring plan with included receipts
                </td>
                <td className="border border-border p-2">
                  Automatic renewal unless cancelled
                </td>
              </tr>
            </tbody>
          </table>
          <p>
            Unused receipt credits may expire after the validity period
            specified in your account.
            <br />
            All prices include applicable taxes unless stated otherwise.
          </p>
          <p>
            Les crédits non utilisés peuvent expirer après la période indiquée
            sur le compte.
            <br />
            Les tarifs s'entendent toutes taxes comprises sauf mention
            contraire.
          </p>
          <p>
            Cancellation of a subscription takes effect at the end of the current
            period.
          </p>

          <p>
            L'annulation d'un abonnement prend effet à la fin de la période en
            cours.
          </p>

          <hr className="my-8" />

          <h2>7️⃣ Intellectual Property / Propriété intellectuelle</h2>
          <p>
            All software, graphics, databases, and algorithms remain the
            exclusive property of TS4U SAS.
            <br />
            Users retain ownership of their uploaded receipts and content.
          </p>
          <p>
            L'ensemble des logiciels, graphismes, bases de données et algorithmes
            demeure la propriété exclusive de TS4U SAS.
            <br />
            Les utilisateurs conservent la propriété de leurs tickets et contenus
            téléversés.
          </p>

          <hr className="my-8" />

          <h2>8️⃣ AI & Analytics Disclaimer / Avertissement IA et analyses</h2>
          <p>
            Insights provided by Cart Xray are generated automatically by AI and
            statistical models.
            <br />
            They are <strong>for informational purposes only</strong> and do not
            constitute medical, nutritional or financial advice.
          </p>
          <p>
            Les analyses fournies par Cart Xray sont générées automatiquement par
            des modèles IA.
            <br />
            Elles sont <strong>strictement informatives</strong> et ne
            constituent ni conseil médical, ni nutritionnel, ni financier.
          </p>

          <hr className="my-8" />

          <h2>9️⃣ Liability / Responsabilité</h2>
          <p>
            TS4U SAS endeavours to maintain continuous, accurate service but
            provides no guarantee of error-free or uninterrupted operation.
            <br />
            Liability is limited to direct damages and cannot exceed the amount
            paid by the user during the twelve (12) months preceding the event.
          </p>
          <p>
            TS4U SAS s'efforce d'assurer un service continu et exact mais ne
            garantit pas une disponibilité ou une exactitude sans erreur.
            <br />
            Sa responsabilité est limitée aux dommages directs et ne peut excéder
            le montant payé par l'utilisateur au cours des douze (12) derniers
            mois.
          </p>

          <hr className="my-8" />

          <h2>🔟 Data Protection / Protection des données</h2>
          <p>
            Processing of personal data is governed by the{" "}
            <strong>Cart Xray Privacy Policy / Politique de confidentialité</strong>{" "}
            (see <Link to="/privacy-policy">Privacy Policy</Link>).
            <br />
            By accepting this EULA, you acknowledge having read and accepted that
            policy.
          </p>
          <p>
            Le traitement des données personnelles est régi par la{" "}
            <strong>Politique de confidentialité Cart Xray</strong> (voir{" "}
            <Link to="/privacy-policy">Politique de confidentialité</Link>).
            <br />
            En acceptant les présentes CGU-EULA, vous reconnaissez en avoir pris
            connaissance et l'avoir acceptée.
          </p>

          <hr className="my-8" />

          <h2>11️⃣ Termination / Résiliation</h2>
          <p>
            Users may delete their account at any time via the app interface.
            <br />
            TS4U SAS may terminate access in case of breach of terms or legal
            obligation.
          </p>
          <p>
            L'utilisateur peut supprimer son compte à tout moment via l'interface
            de l'application.
            <br />
            TS4U SAS peut mettre fin à l'accès en cas de violation des CGU ou
            d'obligation légale.
          </p>

          <hr className="my-8" />

          <h2>
            12️⃣ Governing Law & Jurisdiction / Droit applicable et juridiction
          </h2>
          <p>
            This Agreement is governed by <strong>French law</strong>.
            <br />
            Any dispute shall be brought before the{" "}
            <strong>Tribunal de commerce d'Aix-en-Provence</strong>, unless
            mandatory consumer provisions dictate otherwise.
          </p>
          <p>
            Les présentes CGU-EULA sont soumises au <strong>droit français</strong>
            .<br />
            Tout litige relèvera de la compétence du{" "}
            <strong>Tribunal de commerce d'Aix-en-Provence</strong>, sauf
            dispositions légales impératives contraires applicables aux
            consommateurs.
          </p>

          <hr className="my-8" />

          <h2>13️⃣ Contact / Contact</h2>
          <p>
            For any question or complaint regarding these CGU-EULA or the Privacy
            Policy:
            <br />
            📧 support.corp@cartxray.com
            <br />
            DPO : dpo.corp@cartxray.com
          </p>
          <p>
            Pour toute question ou réclamation relative aux présentes CGU-EULA ou
            à la Politique de confidentialité :
            <br />
            📧 support.corp@cartxray.com
            <br />
            DPO : dpo.corp@cartxray.com
          </p>

          <hr className="my-8" />

          <p className="text-center font-semibold">
            TS4U SAS – Cart Xray © 2025
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
